// ============================================================
// CHANGES SUMMARY (credit repayment phone fix v2):
//
// PROBLEM: For 2nd+ repayments, Phone on SaleOrderPayments row
//   is "N/A". getRepaymentPhone() returned "" → sent "N/A" to SP
//   → SP tried SELECT customer WHERE PhoneNumber = 'N/A' → NULL
//   → CustomerReceivableAccountId = NULL → THROW 50010 → no journal.
//
// FIX: Added fetchCreditInvoicePhone() which queries the original
//   PaymentMethodId=3 row for this order to get the real phone.
//   resolveRepaymentPhone() uses: UI phone > DB invoice row > error.
//
// CHANGES (handlers only — no UI, state, or other logic touched):
//   1. fetchCreditInvoicePhone() — new helper, queries SaleOrderPayments
//   2. resolveRepaymentPhone()   — new helper, replaces getRepaymentPhone()
//   3. handleCashPayment  — uses resolveRepaymentPhone() for isCredit path
//   4. handleBankPayment  — uses resolveRepaymentPhone() for isCredit path
//   5. handleCodePayment  — uses resolveRepaymentPhone() for isCredit path
//
// Everything else (UI, state, receipt, validation, Mpesa, credit
//   invoice creation) is identical to doc 5.
// ============================================================

import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Divider,
  Paper,
  Chip,
  Alert,
  Stack,
  CircularProgress,
  Snackbar,
  Autocomplete,
  InputAdornment,
  IconButton,
  Collapse,
  Fade,
  Zoom,
  Tabs,
  Tab,
  Card,
  CardContent,
} from "@mui/material";
import PaymentIcon from "@mui/icons-material/Payment";
import MoneyIcon from "@mui/icons-material/Money";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DiscountIcon from "@mui/icons-material/LocalOffer";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import WarningIcon from "@mui/icons-material/Warning";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { mpesaPay } from "../../../data/DynamicTable/DynamicFunctions";
import {
  companyManagementUrl,
  customerservice,
  salesManagement,
} from "../../../config";
import {
  fetchDataEngineBalance,
  fetchDataEngineorder,
  generateAndExecuteMutation,
  generateAndExecuteMutationMarkPaid,
  graphqlQuery,
} from "../../../data/Axios/DynamicService";
import eventEmitter from "../../global/components/eventEmitter";
import Swal from "sweetalert2";

const Payment = ({
  open,
  onClose,
  onConfirmPayment,
  cart = [],
  totalAmount: propTotalAmount,
  saleOrderPayment = false,
  saleOrderPaymentAmount = 0,
  branchName = "",
  isWarehouse = false,
  onClearCart,
  customerPhone = "",
  requireMpesaCodeForCash = false,
}) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);
  const orderId = JSON.parse(localStorage.getItem("orderId"));
  const isCredit = Boolean(saleOrderPayment);
  const branchId = parseInt(localStorage.getItem("pos_selected_branch_id"));

  const theme = {
    primary: "#5DADE2",
    light: "#85C1E9",
    lighter: "#AED6F1",
    dark: "#3498DB",
    success: "#4CAF50",
    warning: "#FFA726",
    discount: "#27AE60",
    error: "#E74C3C",
    override: "#FF7043",
    bank: "#1565C0",
  };

  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState([]);
  const [amounts, setAmounts] = useState({
    cash: "",
    mpesa: "",
    credit: "",
    code: "",
    bank: "",
  });
  const [totalAmount, setTotalAmount] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [referenceCode, setReferenceCode] = useState("");
  const [bankReference, setBankReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isShiftOpen, setIsShiftOpen] = useState(false);
  const [cashTendered, setCashTendered] = useState("");
  const [cashMpesaCode, setCashMpesaCode] = useState("");
  const [showChangeCalculation, setShowChangeCalculation] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [generatedReceipt, setGeneratedReceipt] = useState(null);
  const [generatedReceipt2, setGeneratedReceipt2] = useState(null);
  const [showReceipts, setShowReceipts] = useState(false);
  const [isCreditSaleCreated, setIsCreditSaleCreated] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [branchInfo, setBranchInfo] = useState({
    branchName: "",
    building: "",
    company: "",
    contacts: "",
    location: "",
    isWarehouse: false,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    duration: 50000,
  });
  const [error, setError] = useState("");
  const [showMpesaVerifyDialog, setShowMpesaVerifyDialog] = useState(false);
  const [suspendedC2BResults, setSuspendedC2BResults] = useState([]);
  const [suspendedC2BLoading, setSuspendedC2BLoading] = useState(false);
  const [pendingMpesaAmount, setPendingMpesaAmount] = useState(0);
  const [phoneError, setPhoneError] = useState("");

const mpesaResolveRef = useRef(null); // lets manual selection resolve the same promise the poller uses

  const intervalRef = useRef(null);
  const isClosingRef = useRef(false);
  const cashManuallyEdited = useRef(false);
  // ---------------------------------------------------------------------------
  // Checks that a typed phone number is a plausible Kenyan mobile number.
  // Purely for input validation — does NOT alter what gets sent to the backend.
  // Accepts: 0712345678, 0112345678, 712345678, 254712345678, +254712345678
  // ---------------------------------------------------------------------------
  const isValidKenyanPhone = (rawPhone) => {
    if (!rawPhone) return false;
    const cleaned = String(rawPhone).trim().replace(/[\s\-()]/g, "");
    const patterns = [
      /^0[71]\d{8}$/,      // 0712345678 / 0112345678
      /^[71]\d{8}$/,        // 712345678 / 112345678
      /^254[71]\d{8}$/,     // 254712345678
      /^\+254[71]\d{8}$/,   // +254712345678
    ];
    return patterns.some((p) => p.test(cleaned));
  };
  const handlePhoneChange = (value) => {
  setPhoneNumber(value); // unchanged — raw value still goes to backend
  if (!value) {
    setPhoneError("");
    return;
  }
  setPhoneError(isValidKenyanPhone(value) ? "" : "Enter a valid phone number, e.g. 0712345678");
};
  // ---------------------------------------------------------------------------
  // Fetch branch info — unchanged from doc 5
  // ---------------------------------------------------------------------------
  const fetchBranchInfo = useCallback(async () => {
    const query = `query { branchByID(branch: { id: ${branchId} }) { branchName building company contacts location isWarehouse } }`;
    try {
      const result = await graphqlQuery(query, companyManagementUrl.uri);
      if (result && typeof result === "object") {
        setBranchInfo({
          branchName: result.branchName || branchName || "",
          building: result.building || "",
          company: result.company || "",
          contacts: result.contacts || "",
          location: result.location || "",
          isWarehouse: Boolean(result.isWarehouse) || isWarehouse || false,
        });
      }
    } catch (error) {
      console.error("Error fetching branch info:", error);
    }
  }, [branchId, branchName, isWarehouse]);

  // ---------------------------------------------------------------------------
  // Discount / override helpers — unchanged
  // ---------------------------------------------------------------------------
  const calculateDiscountInfo = () => {
    const subtotal = cart.reduce((sum, item) => sum + (item.originalAmount || item.totalPrice || 0), 0);
    const totalDiscounts = cart.reduce((sum, item) => sum + (item.discountValue || 0), 0);
    const hasDiscounts = cart.some((item) => item.isDiscount);
    const discountedItemsCount = cart.filter((item) => item.isDiscount).length;
    return { subtotal, totalDiscounts, hasDiscounts, discountedItemsCount };
  };

  const calculateOverrideInfo = () => {
    const hasOverrides = cart.some((item) => item.isPriceOverride);
    const overriddenItemsCount = cart.filter((item) => item.isPriceOverride).length;
    const totalOverrideAmount = cart.reduce((sum, item) => sum + (item.overrideAmount || 0), 0);
    const originalTotalBeforeOverride = cart.reduce((sum, item) => sum + (item.originalSellingPrice || item.totalPrice || 0), 0);
    return { hasOverrides, overriddenItemsCount, totalOverrideAmount, originalTotalBeforeOverride };
  };

  const discountInfo = calculateDiscountInfo();
  const overrideInfo = calculateOverrideInfo();

  // ---------------------------------------------------------------------------
  // Total amount calculation — unchanged
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let calculatedTotal = cart.reduce((acc, item) => acc + (item.netAmount || item.totalPrice || 0), 0);
    if (selectedCustomer) {
      const { customerTypeId, discountTypeId, discount } = selectedCustomer;
      if ((customerTypeId === 1 || customerTypeId === 2) && discount) {
        if (discountTypeId === 1) calculatedTotal -= (discount / 100) * calculatedTotal;
        else if (discountTypeId === 2) calculatedTotal -= discount;
      }
    }
    if (saleOrderPayment && saleOrderPaymentAmount > 0) calculatedTotal = saleOrderPaymentAmount;
    setTotalAmount(calculatedTotal);
  }, [cart, saleOrderPayment, saleOrderPaymentAmount, selectedCustomer]);

  // ---------------------------------------------------------------------------
  // Receipt generators — unchanged
  // ---------------------------------------------------------------------------
  const generateReceipt = () => {
    const date = new Date().toLocaleString();
    const cashierName = decodedToken.Username || "Cashier";
    const customerName = selectedCustomer?.customerName || newCustomerName || "Cash Customer";
    const receiptData = {
      orderId,
      totalInputAmount: totalAmount.toFixed(2),
      amountPaid: getTotalSplit().toFixed(2),
      date,
      cashierName,
      branchInfo,
      customerName,
      items: cart.map((item) => ({
        name: item.productName || item.name || "Unknown Product",
        quantity: item.quantity || 0,
        unitPrice: item.averageSellingPrice || item.averageBP || item.averageSP || item.unitPrice || 0,
        size: item.size || item.productSize || 0,
        color: item.colorName || item.color || item.productColor || "",
        productCode: item.productCode || "",
      })),
    };
    setGeneratedReceipt(receiptData);
    return "";
  };

  const generateReceipt2 = () => {
    const date = new Date().toLocaleString();
    const cashierName = decodedToken.Username || "Cashier";
    const paymentMethod = selectedPaymentMethods.map((m) => m.charAt(0).toUpperCase() + m.slice(1)).join(" & ") || "Multiple Payments";
    const paymentBreakdown = {};
    selectedPaymentMethods.forEach((method) => {
      if (amounts[method]) paymentBreakdown[method] = parseFloat(amounts[method]).toFixed(2);
    });
    const receiptData = {
      orderId,
      totalInputAmount: totalAmount.toFixed(2),
      amountPaid: getTotalSplit().toFixed(2),
      customerName: selectedCustomer?.customerName || newCustomerName || "Cash Customer",
      date,
      cashierName,
      branchInfo,
      items: cart.map((item) => ({
        name: item.name || item.productName,
        quantity: item.quantity,
        unitPrice: item.averageSellingPrice || item.averageBP || 0,
        size: item.size || item.productSize || 0,
        color: item.color || item.colorName || "",
        productCode: item.productCode || "",
      })),
      paymentMethod,
      paymentBreakdown,
      branchName: branchInfo.branchName || branchName || "Main Branch",
    };
    setGeneratedReceipt2(receiptData);
    return "";
  };

  // ---------------------------------------------------------------------------
  // Misc helpers — unchanged
  // ---------------------------------------------------------------------------
  const calculateChange = () => {
    const tendered = parseFloat(cashTendered) || 0;
    const cashAmount = parseFloat(amounts.cash) || totalAmount;
    return tendered - cashAmount;
  };

  const fetchCustomers = useCallback(async () => {
    const query = `query AllCustomers { allCustomers { id customerName phoneNumber companyName isActive creditApproved customerTypeId discountTypeId discount } }`;
    try {
      const result = await graphqlQuery(query, customerservice.uri);
      setCustomerOptions(result || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
      showSnackbar("Failed to load customers", "error");
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchCustomers();
      fetchBranchInfo();
    }
  }, [open, fetchCustomers, fetchBranchInfo]);
  // ---------------------------------------------------------------------------
// NEW: Manual M-Pesa verification via latestIncomingSuspendedC2B
// Formats "now" as "YYYY-MM-DD HH:mm:ss.SSS" to match the SP's expected format.
// ---------------------------------------------------------------------------
const getFormattedDateTime = () => {
  const d = new Date();
  const pad = (n, z = 2) => String(n).padStart(z, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const fetchSuspendedC2BTransactions = useCallback(
  async (amount) => {
    setSuspendedC2BLoading(true);
    const transactionTime = getFormattedDateTime();
    const query = `
      query {
        latestIncomingSuspendedC2B(
          functionparams: { amount: ${amount}, companyId: ${companyId}, transactionTime: "${transactionTime}" }
        ) {
          billRefNumber branchId businessShortCode company companyId createdBy createdDate
          firstName id invoiceNumber isPosted isPostedDate isReconcilled isSuspended lastName
          loanRef messageArrivalTime middleName migId msisdn orgAccountBalance reconcilledDate
          thirdPartyTransID timeDifferenceSeconds transactionType transAmount transID transTime
          transType verified
        }
      }`;
    try {
      const result = await graphqlQuery(query, salesManagement.uri);
      setSuspendedC2BResults(result || []);
    } catch (error) {
      console.error("Error fetching suspended C2B transactions:", error);
      setSuspendedC2BResults([]);
    } finally {
      setSuspendedC2BLoading(false);
    }
  },
  [companyId],
);

const handleSelectSuspendedTransaction = async (transaction) => {
  clearInterval(intervalRef.current);
  setSuspendedC2BLoading(true);
  const success = await handleCodePayment({
    referenceCode: transaction.transID,
    amount: transaction.transAmount,
    phone: phoneNumber, // the number the STK push was requested to, not the C2B msisdn
  });
  setSuspendedC2BLoading(false);

  if (!success) {
    showSnackbar("Could not record payment for the selected transaction. Please try again or pick another.", "error");
    return;
  }

  setIsConfirming(false);
  showSnackbar(`Payment confirmed via transaction ${transaction.transID || transaction.id}`, "success");
  if (mpesaResolveRef.current) {
    mpesaResolveRef.current(true);
  }
};

const handleCloseVerifyDialog = () => {
  // Just hides the popup — automatic polling keeps running in the background.
  setShowMpesaVerifyDialog(false);
};

const handleCancelMpesaVerification = () => {
  // Fully aborts: stops polling and resolves the pending payment as failed.
  clearInterval(intervalRef.current);
  setIsConfirming(false);
  setShowMpesaVerifyDialog(false);
  showSnackbar("M-Pesa payment verification cancelled", "warning");
  if (mpesaResolveRef.current) {
    mpesaResolveRef.current(false);
  }
};

  const showSnackbar = (message, severity = "success", duration = 20000) => {
    if (isClosingRef.current) return;
    setSnackbar({ open: true, message, severity, duration });
  };

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  const checkShiftBalance = async () => {
    try {
      await fetchDataEngineBalance(
        "openingClosingBalance", "functionparams",
        { companyId, createdBy: userId, manualOpeningBalance: null },
        ["openingBalance", "closingBalance", "cashSales", "creditSales", "mpesaSales"],
        salesManagement.uri,
      );
      setIsShiftOpen(true);
    } catch (error) {
      await Swal.fire({ icon: "error", title: "Shift not started", text: "Please open a shift before making payments." });
      setIsShiftOpen(false);
    }
  };

  useEffect(() => { checkShiftBalance(); }, []);

  const getTotalSplit = () =>
    selectedPaymentMethods.reduce((sum, method) => sum + (parseFloat(amounts[method]) || 0), 0);

  useEffect(() => {
    if (selectedPaymentMethods.includes("cash")) {
      setShowChangeCalculation(true);
      if (!isCredit && !amounts.cash) {
        const requiredAmount = selectedPaymentMethods.length === 1
          ? totalAmount
          : totalAmount - selectedPaymentMethods
              .filter((m) => m !== "cash")
              .reduce((sum, m) => sum + (parseFloat(amounts[m]) || 0), 0);
        setAmounts((prev) => ({ ...prev, cash: requiredAmount.toFixed(2) }));
      }
    } else {
      setShowChangeCalculation(false);
      setCashTendered("");
    }
  }, [selectedPaymentMethods, totalAmount, isCredit]);

  const handlePaymentMethodToggle = (method) => {
    setSelectedPaymentMethods((prev) => {
      if (prev.includes(method)) {
        setAmounts((prevAmounts) => ({ ...prevAmounts, [method]: "" }));
        return prev.filter((m) => m !== method);
      }
      return [...prev, method];
    });
    setError("");
  };

  const handleAmountChange = (method, value) => {
    const numericValue = Number(value) || 0;
    setAmounts((prev) => {
      const updated = { ...prev, [method]: numericValue };
      const mpesa = updated.mpesa || 0;
      const code = updated.code || 0;
      if (!isCredit && (mpesa > 0 || code > 0) && selectedPaymentMethods.includes("cash")) {
        const cash = totalAmount - (mpesa + code);
        return { ...updated, cash: cash >= 0 ? cash : 0 };
      }
      return updated;
    });
  };

  const createCustomer = async () => {
    if (!newCustomerName.trim() || !newPhoneNumber.trim()) {
      showSnackbar("Customer name and phone number are required", "error");
      return null;
    }
    const CREATE_CUSTOMER_MUTATION = `
      mutation Createcustomers($customers: CustomersInput!) {
        createcustomers(customers: $customers) { id customerName phoneNumber isActive creditApproved }
      }`;
    try {
      setLoading(true);
      const token = localStorage.getItem("token")?.replace(/"/g, "");
      const response = await axios.post(
        customerservice.uri,
        {
          query: CREATE_CUSTOMER_MUTATION,
          variables: {
            customers: {
              customerName: newCustomerName,
              phoneNumber: newPhoneNumber,
              createdBy: userId,
              isActive: true,
              creditApproved: true,
              customerTypeId: 0,
            },
          },
        },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } },
      );
      const createdCustomer = response.data.data.createcustomers;
      setCustomerOptions((prev) => [...prev, createdCustomer]);
      setSelectedCustomer(createdCustomer);
      setPhoneNumber(createdCustomer.phoneNumber);
      showSnackbar("Customer created successfully", "success");
      return createdCustomer;
    } catch (error) {
      showSnackbar("Error creating customer", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // FIX: Phone resolution for credit repayments
  //
  // fetchCreditInvoicePhone — queries the original PaymentMethodId=3 row for
  //   this order, which always holds the real customer phone set at invoice time.
  //   This is the authoritative source; the repayment rows store "N/A".
  //
  // resolveRepaymentPhone — three-level priority:
  //   1. Phone typed into the UI by the cashier (phoneNumber state)
  //   2. Phone from the original credit invoice row in the DB
  //   3. null  →  caller shows a meaningful error and aborts
  //
  // getRepaymentPhone is kept unchanged so nothing else breaks if referenced.
  // ---------------------------------------------------------------------------
  const getRepaymentPhone = () => phoneNumber || "";


const resolveRepaymentPhone = () => {
  console.log("📞 resolveRepaymentPhone called - phoneNumber:", phoneNumber, "customerPhone:", customerPhone);
  // 1. Phone typed by cashier
  if (phoneNumber && phoneNumber !== "" && phoneNumber !== "") {
    console.log("✅ Using typed phoneNumber:", phoneNumber);
    return phoneNumber;
  }

  // 2. Phone passed from parent
  if (customerPhone && customerPhone !== "" && customerPhone !== "") {
    console.log("✅ Using customerPhone from prop:", customerPhone);
    return customerPhone;
  }

  // 3. Customer selected from dropdown
  if (selectedCustomer?.phoneNumber &&
      selectedCustomer.phoneNumber !== "" &&
      selectedCustomer.phoneNumber !== "") {
    console.log("✅ Using selectedCustomer phone:", selectedCustomer.phoneNumber);
    return selectedCustomer.phoneNumber;
  }

  console.warn("❌ No phone available - returning null");
  return null;
};
  // ---------------------------------------------------------------------------
  // M-Pesa — unchanged (already requires phone explicitly from UI state)
  // ---------------------------------------------------------------------------
  const handleMpesaPayment = async () => {
    // if (!phoneNumber) {
    //   showSnackbar("Phone number is required for M-Pesa", "error");
    //   return false;
    // }
    if (!isValidKenyanPhone(phoneNumber)) {
    showSnackbar("Enter a valid phone number for M-Pesa", "error");
    return false;
  }
    const mpesaAmount = parseFloat(amounts.mpesa || totalAmount);
    if (mpesaAmount > totalAmount) {
      showSnackbar("M-Pesa amount cannot exceed total", "error");
      return false;
    }
    try {
      setLoading(true);
      const response = await mpesaPay(orderId, mpesaAmount, phoneNumber, branchId);
      if (response.status === 200) {
      setLoading(false);
      setIsConfirming(true);
      showSnackbar("Payment initiated. Check your phone to complete...", "info");

      // NEW: open the verification popup and look for a matching incoming transaction
      setPendingMpesaAmount(mpesaAmount);
      setShowMpesaVerifyDialog(true);
      fetchSuspendedC2BTransactions(mpesaAmount);

      return new Promise((resolve) => {
        let settled = false;
        const finish = (value) => {
          if (settled) return;
          settled = true;
          mpesaResolveRef.current = null;
          setShowMpesaVerifyDialog(false);
          resolve(value);
        };
        mpesaResolveRef.current = finish; // manual card selection calls this

        let attempts = 0;
        const maxAttempts = 10;
        const pollInterval = 5000;
        const checkPayment = async () => {
          try {
            attempts++;
            const isPaid = await fetchDataEngineorder("isSaleOrderPaid", { saleOrderId: orderId }, salesManagement.uri);
            if (isPaid) {
              // Only the explicit "paid" status auto-closes the dialog.
              clearInterval(intervalRef.current);
              setIsConfirming(false);
              showSnackbar("Payment confirmed successfully!", "success");
              finish(true);
            } else if (attempts >= maxAttempts) {
              // Stop auto-polling but leave the dialog open — only the user
              // (pick a transaction or hit Cancel) or a confirmed "paid" status
              // should close it from here.
              clearInterval(intervalRef.current);
              showSnackbar("Still waiting for M-Pesa confirmation. Select the matching transaction below, or cancel.", "warning");
            }
          } catch (error) {
            if (attempts >= maxAttempts) {
              clearInterval(intervalRef.current);
              showSnackbar("Unable to auto-verify payment. Select the matching transaction below, or cancel.", "warning");
            }
          }
        };
        setTimeout(() => {
          checkPayment();
          intervalRef.current = setInterval(checkPayment, pollInterval);
        }, 5000);
      });
    }
      setLoading(false);
      return false;
    } catch (errors) {
      setLoading(false);
      setIsConfirming(false);
      setShowMpesaVerifyDialog(false); // NEW
      const errorMessages = errors.response?.data?.errors?.map((e) => e.message).join("; ") || errors.message;
      showSnackbar(`M-Pesa payment failed: ${errorMessages}`);
      return false;
    }
  };

  // ---------------------------------------------------------------------------
  // Cash payment
  // CHANGED: isCredit path now calls resolveRepaymentPhone() instead of
  //   getRepaymentPhone(), guaranteeing a real phone is sent to the SP.
  //   Non-credit path (Cash Customer lookup) is identical to doc 5.
  // ---------------------------------------------------------------------------
  const handleCashPayment = async () => {
    const cashAmount = parseFloat(amounts.cash || totalAmount);
    try {
      setLoading(true);

      let paymentPhone;
      if (isCredit) {
        // Repayment: resolve phone from UI → DB invoice row → error
        paymentPhone = await resolveRepaymentPhone();
        if (!paymentPhone) {
          showSnackbar(
            "Could not resolve customer phone for this credit order. Please contact support.",
            "error",
          );
          return false;
        }
      } else {
        // New cash sale: use Cash Customer's phone (doc 5 pattern)
        const customers = await graphqlQuery(
          `query AllCustomers { allCustomers { id customerName phoneNumber isActive creditApproved } }`,
          customerservice.uri,
        );
        const foundCustomer = customers?.filter((c) =>
          c.customerName.toLowerCase().includes("cash customer"),
        );
        paymentPhone = foundCustomer[0]?.phoneNumber || "";
      }

      if (newCustomerName && newPhoneNumber) await createCustomer();

      const response = await generateAndExecuteMutationMarkPaid(
        "markSalePaid",
        {
          orderId,
          isCreditPayment: isCredit ? 1 : 0,
          amountPaid: cashAmount,
          phone: paymentPhone,
          paymentMethodId: 2,
          ...(requireMpesaCodeForCash && cashMpesaCode ? { mpesaCode: cashMpesaCode } : {}),
        },
        "sale",
        salesManagement.uri,
      );
      return !!response;
    } catch (errors) {
      const errorMessages = errors.response?.data?.errors?.map((e) => e.message).join("; ") || errors.message;
      showSnackbar(`Cash payment failed: ${errorMessages}`, "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Bank payment
  // CHANGED: isCredit path now calls resolveRepaymentPhone().
  //   Non-credit path is identical to doc 5.
  // ---------------------------------------------------------------------------
  const handleBankPayment = async () => {
    const bankAmount = parseFloat(amounts.bank || totalAmount);
    try {
      setLoading(true);

      let paymentPhone;
      if (isCredit) {
        paymentPhone = await resolveRepaymentPhone();
        if (!paymentPhone) {
          showSnackbar(
            "Could not resolve customer phone for this credit order. Please contact support.",
            "error",
          );
          return false;
        }
      } else {
        const customers = await graphqlQuery(
          `query AllCustomers { allCustomers { id customerName phoneNumber isActive creditApproved } }`,
          customerservice.uri,
        );
        const foundCustomer = customers?.filter((c) =>
          c.customerName.toLowerCase().includes("cash customer"),
        );
        paymentPhone = foundCustomer[0]?.phoneNumber || "";
      }

      if (newCustomerName && newPhoneNumber) await createCustomer();

      const response = await generateAndExecuteMutationMarkPaid(
        "markSalePaid",
        {
          orderId,
          isCreditPayment: isCredit ? 1 : 0,
          amountPaid: bankAmount,
          phone: paymentPhone,
          paymentMethodId: 4,
        },
        "sale",
        salesManagement.uri,
      );
      if (response) {
        showSnackbar(`Bank payment recorded. Ref: ${bankReference}`, "success");
        return true;
      }
      return false;
    } catch (errors) {
      const errorMessages = errors.response?.data?.errors?.map((e) => e.message).join("; ") || errors.message;
      showSnackbar(`Bank payment failed: ${errorMessages}`, "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Credit invoice creation — unchanged (isCreditPayment: 0, PaymentMethodId: 3)
  // ---------------------------------------------------------------------------
  const handleCreditPayment = async () => {
    if (!selectedCustomer && !newCustomerName) {
      showSnackbar("Customer selection required for credit", "error");
      return false;
    }

    const creditAmount = parseFloat(amounts.credit || totalAmount);
    let customerToUse = selectedCustomer;

    try {
      setLoading(true);
      if (newCustomerName && newPhoneNumber) {
        customerToUse = await createCustomer();
        if (!customerToUse) return false;
      }

      const customerPhone = customerToUse?.phoneNumber || newPhoneNumber || "";
      const customerName = customerToUse?.customerName || newCustomerName;

      const response = await generateAndExecuteMutationMarkPaid(
        "markSalePaid",
        {
          orderId,
          isCreditPayment: 0,  // New credit sale (not a repayment)
          amountPaid: creditAmount,
          paymentMethodId: 3,
          name: customerName,
          phone: customerPhone,
        },
        "sale",
        salesManagement.uri,
      );
      return !!response;
    } catch (errors) {
      const errorMessages = errors.response?.data?.errors?.map((e) => e.message).join("; ") || errors.message;
      showSnackbar(`Credit payment failed: ${errorMessages}`, "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // M-Pesa Code payment
  // CHANGED: isCredit path now calls resolveRepaymentPhone() when phoneNumber
  //   is empty, instead of getRepaymentPhone() which returned "N/A".
  //   Non-credit path is identical to doc 5.
  // ---------------------------------------------------------------------------
  const handleCodePayment = async (overrides = {}) => {
    const codeToUse = overrides.referenceCode ?? referenceCode;
    if (!codeToUse) {
      showSnackbar("M-Pesa code is required", "error");
      return false;
    }
    const codeAmount = parseFloat(overrides.amount ?? amounts.code ?? totalAmount) || totalAmount;
    try {
      setLoading(true);

      let paymentPhone = overrides.phone || phoneNumber;

      if (isCredit && (!paymentPhone || paymentPhone === "" || paymentPhone === "")) {
        // Repayment with no phone typed: look up from the invoice row
        paymentPhone = await resolveRepaymentPhone();
        if (!paymentPhone) {
          showSnackbar(
            "Could not resolve customer phone for this credit order. Please contact support.",
            "error",
          );
          return false;
        }
      }

      if (newCustomerName && newPhoneNumber) await createCustomer();

      const response = await generateAndExecuteMutationMarkPaid(
        "markSalePaid",
        {
          orderId,
          isCreditPayment: isCredit ? 1 : 0,
          amountPaid: codeAmount,
          referenceCode: codeToUse,
          phone: paymentPhone || " ",
          paymentMethodId: 16,
        },
        "sale",
        salesManagement.uri,
      );
      return !!response;
    } catch (errors) {
      const errorMessages = errors.response?.data?.errors?.map((e) => e.message).join("; ") || errors.message;
      showSnackbar(`Code payment failed: ${errorMessages}`, "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Validation — unchanged
  // ---------------------------------------------------------------------------
  const validatePaymentReadiness = () => {
    const errors = [];
    if (!isShiftOpen) errors.push("Shift must be open");
    if (selectedPaymentMethods.length === 0) errors.push("Select at least one payment method");
    const totalSplit = getTotalSplit();
    if (!isCredit && totalSplit < totalAmount) errors.push("Payment amount must cover the total");
    if (totalSplit > totalAmount) errors.push("Payment amount exceeds total");
    if (isCredit && totalSplit <= 0) errors.push("Please enter an amount to pay");
    if (selectedPaymentMethods.includes("credit")) {
      if (!selectedCustomer && !newCustomerName && !newPhoneNumber)
        errors.push("Customer must be selected for credit payment");
    }
    if (selectedPaymentMethods.includes("mpesa") && !phoneNumber)
      errors.push("Phone number required for M-Pesa");
    if (selectedPaymentMethods.includes("code") && !referenceCode)
      errors.push("M-Pesa code is required");
    if (selectedPaymentMethods.includes("mpesa") && !isValidKenyanPhone(phoneNumber))
      errors.push("Enter a valid phone number for M-Pesa");
    // if (requireMpesaCodeForCash && selectedPaymentMethods.includes("cash") && !cashMpesaCode)
    //   errors.push("M-Pesa code is required for cash sales");
    return errors;
  };

  // ---------------------------------------------------------------------------
  // Confirm payment — unchanged
  // ---------------------------------------------------------------------------
  const handleConfirmPayment = async () => {
    const validationErrors = validatePaymentReadiness();
    if (validationErrors.length > 0) {
      showSnackbar(validationErrors[0], "error");
      return;
    }

    const totalSplit = getTotalSplit();
    const change = calculateChange();
    const isNewCreditSale = selectedPaymentMethods.includes("credit") && !isCredit;

    let paymentBreakdownHTML = `<div style="text-align: left;">
      <p style="font-size: 18px; margin-bottom: 12px;"><strong>Total Amount:</strong> <span style="color: #3498DB;">KSh ${totalAmount.toFixed(2)}</span></p>
      <p style="font-weight: 600; margin-bottom: 8px;">Payment Breakdown:</p>
      ${selectedPaymentMethods.map((m) => {
        const methodAmount = parseFloat(amounts[m.toLowerCase()]) || 0;
        let extraInfo = "";
        if (m === "bank" && bankReference) extraInfo = ` (Ref: ${bankReference})`;
        else if (m === "code" && referenceCode) extraInfo = ` (Code: ${referenceCode})`;
        return `<p style="margin-left: 20px; margin-bottom: 4px;">• ${m.toUpperCase()}: KSh ${methodAmount.toFixed(2)}${extraInfo}</p>`;
      }).join("")}
      <hr style="border: none; border-top: 2px solid #E0E0E0; margin: 12px 0;" />
      <p style="font-size: 16px;"><strong>Total Split:</strong> KSh ${totalSplit.toFixed(2)}</p>
      ${isNewCreditSale ? `<div style="background: #FFF8E1; padding: 12px; border-radius: 8px; margin-top: 12px; border-left: 4px solid #FFA726;">
            <p style="margin: 0; color: #E65100; font-weight: 600;">⚠️ This will create a credit invoice. The order will remain UNPAID until the customer repays.</p>
           </div>` : ""}
      ${selectedCustomer ? `<p style="margin-top: 12px;"><strong>Customer:</strong> ${selectedCustomer.customerName}</p>` : ""}
    </div>`;

    const result = await Swal.fire({
      title: isNewCreditSale ? "Confirm Credit Sale" : "Confirm Payments",
      html: paymentBreakdownHTML,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isNewCreditSale ? "Create Credit Invoice" : "Confirm Payments",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const results = [];
      if (selectedPaymentMethods.includes("cash")) results.push(await handleCashPayment());
      if (selectedPaymentMethods.includes("bank")) results.push(await handleBankPayment());
      if (selectedPaymentMethods.includes("credit")) results.push(await handleCreditPayment());
      if (selectedPaymentMethods.includes("code")) results.push(await handleCodePayment());
      if (selectedPaymentMethods.includes("mpesa")) results.push(await handleMpesaPayment());

      const allSuccessful = results.every((r) => r);

      if (allSuccessful) {
        generateReceipt();
        generateReceipt2();
        setShowReceipts(true);
        setActiveTab(0);

        if (!isNewCreditSale) {
          eventEmitter.emit("updateBalances");
        }

        setIsCreditSaleCreated(isNewCreditSale);

        const successTitle = isNewCreditSale ? "Credit Invoice Created!" : "Payment Successful!";
        const successText = isNewCreditSale
          ? `Credit invoice created for KSh ${totalAmount.toFixed(2)}. Order is marked as unpaid pending customer repayment.`
          : selectedPaymentMethods.includes("cash") && change > 0
            ? `All payments confirmed.\n\nChange to return: KSh ${change.toFixed(2)}`
            : "All payments confirmed successfully.";

        await Swal.fire({ icon: "success", title: successTitle, text: successText, timer: 3000 });
      }
    } catch (error) {
      showSnackbar("Payment processing failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Receipt done / close — unchanged
  // ---------------------------------------------------------------------------
  const handleReceiptDone = () => {
    if (isCredit) {
      onConfirmPayment();
    } else if (isCreditSaleCreated) {
      onClearCart();
      onConfirmPayment();
      eventEmitter.emit("updateBalances");
    } else {
      onClearCart();
      onConfirmPayment();
    }

    setSelectedPaymentMethods([]);
    setAmounts({ cash: "", mpesa: "", credit: "", code: "", bank: "" });
    setPhoneNumber("");
    setReferenceCode("");
    setBankReference("");
    setSelectedCustomer(null);
    setNewCustomerName("");
    setNewPhoneNumber("");
    setCashTendered("");
    setCashMpesaCode("");
    setGeneratedReceipt(null);
    setGeneratedReceipt2(null);
    setShowReceipts(false);
    setActiveTab(0);
    setIsCreditSaleCreated(false);
    onClose();
  };

  const handleClose = () => {
    isClosingRef.current = true;
    setSelectedPaymentMethods([]);
    setAmounts({ cash: "", mpesa: "", credit: "", code: "", bank: "" });
    setPhoneNumber("");
    setReferenceCode("");
    setBankReference("");
    setSelectedCustomer(null);
    setNewCustomerName("");
    setNewPhoneNumber("");
    setCashTendered("");
    setCashMpesaCode("");
    setGeneratedReceipt(null);
    setGeneratedReceipt2(null);
    setShowReceipts(false);
    setActiveTab(0);
    setIsCreditSaleCreated(false);
    onClose();
    setTimeout(() => { isClosingRef.current = false; }, 100);
  };

  // ---------------------------------------------------------------------------
  // Print receipt — unchanged
  // ---------------------------------------------------------------------------
  const handlePrintReceipt = (type = "simple") => {
    const receiptWindow = window.open("", "PRINT", "height=600,width=400");
    const CompanyName = decodedToken.Company || "Company";
    const date = new Date().toLocaleString();
    const customerName = selectedCustomer?.customerName || newCustomerName || "Cash Customer";

    let receiptContent = "";
    if (type === "simple") {
      receiptContent = `<!DOCTYPE html><html><head><style>
        body { font-family: Arial, sans-serif; line-height: 1.6; margin: 15px; }
        .header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #000; padding-bottom: 10px; }
        .footer { text-align: center; margin-top: 20px; padding-top: 10px; border-top: 1px dashed #000; }
      </style></head><body>
        <div class="header"><h2>${CompanyName}</h2><h3>Cash Sale Receipt</h3>
          <p><strong>Date:</strong> ${date}</p><p><strong>Order ID:</strong> POS${orderId}</p>
          <p><strong>Cashier:</strong> ${decodedToken.Username || "Cashier"}</p>
          <p><strong>Customer:</strong> ${customerName}</p>
          <p><strong>Branch:</strong> ${branchInfo.branchName || branchName || "Main Branch"}</p>
          ${branchInfo ? `<div style="text-align:center;font-size:12px;"><div>Location: ${branchInfo.location || ""} ${branchInfo.building || ""}</div>
          <div>Contacts: ${branchInfo.contacts || ""}</div></div>` : ""}
        </div>
        <div style="margin: 15px 0;">
          <div style="display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; font-weight: bold; border-bottom: 2px solid #333; padding-bottom: 5px; margin-bottom: 10px;">
            <span>Item</span><span style="text-align:center;">Qty</span><span style="text-align:center;">Size/Color</span><span style="text-align:right;">Amount</span>
          </div>
          ${cart.map((item) => {
            const sizeValue = item.size || item.productSize || item.sizeRange || 0;
            const colorValue = item.colorName || item.color || item.productColor || "";
            return `<div style="display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; padding: 5px 0; border-bottom: 1px solid #eee;">
              <span>${item.name || item.productName}</span><span style="text-align:center;">${item.quantity}</span>
              <span style="text-align:center;font-size:11px;">${sizeValue}/${colorValue}</span>
              <span style="text-align:right;">KSh ${((item.averageSellingPrice || item.averageBP || 0) * item.quantity).toFixed(2)}</span>
            </div>`;
          }).join("")}
        </div>
        <div style="border-top: 2px solid #000; padding-top: 10px; margin-top: 15px;">
          <div style="display: flex; justify-content: space-between; margin: 5px 0;"><strong>Total Amount:</strong><strong>KSh ${totalAmount.toFixed(2)}</strong></div>
          <div style="display: flex; justify-content: space-between; margin: 5px 0;"><strong>Amount Paid:</strong><strong>KSh ${getTotalSplit().toFixed(2)}</strong></div>
        </div>
        <div class="footer"><p><strong>Thank you for your purchase!</strong></p><p>${new Date().toLocaleDateString()}</p></div>
      </body></html>`;
    } else {
      receiptContent = `<!DOCTYPE html><html><head><style>
        body { font-family: Arial, sans-serif; line-height: 1.6; margin: 15px; }
        .header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #000; padding-bottom: 10px; }
        .payment-breakdown { background: #f5f5f5; padding: 10px; margin: 10px 0; border-radius: 5px; }
      </style></head><body>
        <div class="header"><h2>${CompanyName}</h2><h3>Detailed Receipt</h3>
          <p><strong>Date:</strong> ${date}</p><p><strong>Order ID:</strong> POS${orderId}</p>
          <p><strong>Cashier:</strong> ${decodedToken.Username || "Cashier"}</p>
          <p><strong>Customer:</strong> ${customerName}</p>
          <p><strong>Branch:</strong> ${branchInfo.branchName || branchName || "Main Branch"}</p>
        </div>
        <div style="margin: 15px 0;">
          ${cart.map((item) => {
            const sizeValue = item.size || item.productSize || item.sizeRange || 0;
            const colorValue = item.colorName || item.color || item.productColor || " ";
            return `<div style="display: flex; justify-content: space-between; margin-bottom: 5px; padding-bottom: 5px; border-bottom: 1px solid #eee;">
              <div><div><strong>${item.name || item.productName}</strong></div>
              <div style="font-size:11px;color:#666;">Qty: ${item.quantity} | Size: ${sizeValue} | Color: ${colorValue}</div>
              <div style="font-size:11px;color:#666;">${item.quantity} × KSh ${item.averageSellingPrice || item.averageBP || 0}</div></div>
              <div style="text-align:right;"><strong>KSh ${((item.averageSellingPrice || item.averageBP || 0) * item.quantity).toFixed(2)}</strong></div>
            </div>`;
          }).join("")}
        </div>
        <div class="payment-breakdown">
          <strong>Payment Breakdown:</strong><br>
          ${selectedPaymentMethods.map((method) =>
            `<div style="display: flex; justify-content: space-between; margin-left: 10px;">
              <span>${method.charAt(0).toUpperCase() + method.slice(1)}:</span>
              <span>KSh ${(parseFloat(amounts[method]) || 0).toFixed(2)}</span>
            </div>`
          ).join("")}
        </div>
        <div style="border-top: 2px solid #000; padding-top: 10px; margin-top: 15px;">
          <div style="display: flex; justify-content: space-between; margin: 5px 0; font-size: 14px;">
            <strong>TOTAL:</strong><strong>KSh ${totalAmount.toFixed(2)}</strong>
          </div>
        </div>
        <div style="text-align:center;margin-top:20px;padding-top:10px;border-top:1px dashed #000;">
          <p><strong>Thank you for your business!</strong></p><p style="font-size:11px;">Receipt ID: POS${orderId}</p>
        </div>
      </body></html>`;
    }

    receiptWindow.document.write(receiptContent);
    receiptWindow.document.close();
    setTimeout(() => {
      receiptWindow.focus();
      receiptWindow.print();
      setTimeout(() => receiptWindow.close(), 1000);
    }, 500);
  };

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  // ---------------------------------------------------------------------------
  // Derived UI values — unchanged
  // ---------------------------------------------------------------------------
  const filteredCustomers = customerOptions.filter(
    (c) => c.isActive && c.creditApproved && c.customerName.toLowerCase().includes(searchInput.toLowerCase()),
  );

  const needsCustomer = selectedPaymentMethods.some((m) => m === "credit" || m === "code");
  const change = calculateChange();
  const showChangeAlert = selectedPaymentMethods.includes("cash") && cashTendered && parseFloat(cashTendered) > 0;

  const isPaymentDisabled =
    loading ||
    isConfirming ||
    selectedPaymentMethods.length === 0 ||
    (!isCredit && getTotalSplit() < totalAmount) ||
    (isCredit && getTotalSplit() <= 0) ||
    getTotalSplit() > totalAmount ||
    // (selectedPaymentMethods.includes("mpesa") && !phoneNumber) ||
    (selectedPaymentMethods.includes("mpesa") && !isValidKenyanPhone(phoneNumber)) ||
    (selectedPaymentMethods.includes("code") && !referenceCode) ||
    (selectedPaymentMethods.includes("credit") && !selectedCustomer && !newCustomerName);
    
const formatTransactionTime = (value) => {
  if (!value || String(value).length !== 14) return value;

  const str = String(value);

  const date = new Date(
    Number(str.substring(0, 4)),
    Number(str.substring(4, 6)) - 1,
    Number(str.substring(6, 8)),
    Number(str.substring(8, 10)),
    Number(str.substring(10, 12)),
    Number(str.substring(12, 14))
  );

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};
  // ---------------------------------------------------------------------------
  // Render — identical to doc 5
  // ---------------------------------------------------------------------------
  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => { if (reason !== "backdropClick") handleClose(); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: theme.primary, color: "white", display: "flex", alignItems: "center", gap: 1 }}>
          <PaymentIcon />
          <Typography variant="h5" fontWeight="600">
            {isCredit ? "Credit Repayment" : "Payment"} - {branchName}
          </Typography>
          {isCredit && (
            <Chip label="Repaying Credit" size="small" icon={<CreditScoreIcon style={{ color: "white" }} />}
              sx={{ ml: 1, bgcolor: "#FFA726", color: "white", fontWeight: "600" }} />
          )}
          {showReceipts && (
            <Button size="small" variant="contained"
              onClick={() => { setShowReceipts(false); setActiveTab(0); }}
              sx={{ ml: "auto", bgcolor: "white", color: theme.primary, "&:hover": { bgcolor: "#f5f5f5" } }}>
              Back to Payment
            </Button>
          )}
        </DialogTitle>

        {showReceipts ? (
          <DialogContent sx={{ mt: 2 }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
              <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                <Tab label="Simple Receipt" icon={<ReceiptIcon />} iconPosition="start" />
                <Tab label="Detailed Receipt" icon={<ReceiptLongIcon />} iconPosition="start" />
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <Card sx={{ mb: 3, border: `2px solid ${theme.primary}` }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h6" color={theme.primary}>Simple Receipt Preview</Typography>
                    <Button variant="contained" startIcon={<PrintIcon />} onClick={() => handlePrintReceipt("simple")} sx={{ bgcolor: theme.primary }}>Print Receipt</Button>
                  </Box>
                  <Paper sx={{ p: 3, bgcolor: "#fafafa", border: "1px solid #ddd" }}>
                    <Box sx={{ textAlign: "center", mb: 2, borderBottom: "2px solid #000", pb: 2 }}>
                      <Typography variant="h5" fontWeight="bold">{decodedToken.Company || "Company"}</Typography>
                      <Typography variant="subtitle1">{isCreditSaleCreated ? "Credit Invoice" : "Cash Sale Receipt"}</Typography>
                      {branchInfo && (<><Typography variant="body2"><strong>Branch:</strong> {branchInfo.branchName || branchName}</Typography>
                        <Typography variant="body2"><strong>Location:</strong> {branchInfo.location} {branchInfo.building}</Typography>
                        <Typography variant="body2"><strong>Contact:</strong> {branchInfo.contacts}</Typography></>)}
                      <Typography variant="body2"><strong>Date:</strong> {new Date().toLocaleString()}</Typography>
                      <Typography variant="body2"><strong>Order ID:</strong> POS{orderId}</Typography>
                      <Typography variant="body2"><strong>Cashier:</strong> {decodedToken.Username || "Cashier"}</Typography>
                      <Typography variant="body2"><strong>Customer:</strong> {selectedCustomer?.customerName || newCustomerName || "Cash Customer"}</Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      {cart.map((item, index) => (
                        <Box key={index} sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: "1px dashed #ddd" }}>
                          <Typography variant="body2">{item.name || item.productName}</Typography>
                          <Typography variant="body2">x{item.quantity}</Typography>
                          <Typography variant="body2" fontWeight="bold">KSh {((item.averageSellingPrice || item.averageBP || 0) * item.quantity).toFixed(2)}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ borderTop: "2px solid #000", pt: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="body1"><strong>Total Amount:</strong></Typography>
                        <Typography variant="body1" fontWeight="bold">KSh {totalAmount.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="body1"><strong>{isCreditSaleCreated ? "Amount Invoiced:" : "Amount Paid:"}</strong></Typography>
                        <Typography variant="body1" fontWeight="bold" color={isCreditSaleCreated ? theme.warning : theme.success}>KSh {getTotalSplit().toFixed(2)}</Typography>
                      </Box>
                      {isCreditSaleCreated && (
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <Typography variant="body1" color="error"><strong>Outstanding:</strong></Typography>
                          <Typography variant="body1" fontWeight="bold" color="error">KSh {totalAmount.toFixed(2)}</Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ textAlign: "center", mt: 3, pt: 2, borderTop: "1px dashed #000" }}>
                      <Typography variant="body2" fontWeight="bold">
                        {isCreditSaleCreated ? "Credit invoice created — payment pending." : "Thank you for your purchase!"}
                      </Typography>
                      <Typography variant="caption">{new Date().toLocaleDateString()}</Typography>
                    </Box>
                  </Paper>
                </CardContent>
              </Card>
            )}

            {activeTab === 1 && (
              <Card sx={{ mb: 3, border: `2px solid ${theme.bank}` }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h6" color={theme.bank}>Detailed Receipt Preview</Typography>
                    <Button variant="contained" startIcon={<PrintIcon />} onClick={() => handlePrintReceipt("detailed")} sx={{ bgcolor: theme.bank }}>Print Receipt</Button>
                  </Box>
                  <Paper sx={{ p: 3, bgcolor: "#fafafa", border: "1px solid #ddd" }}>
                    <Box sx={{ textAlign: "center", mb: 3, borderBottom: "2px solid #000", pb: 2 }}>
                      <Typography variant="h5" fontWeight="bold">{decodedToken.Company || "Company"}</Typography>
                      <Typography variant="subtitle1" color={theme.bank}>Detailed Receipt</Typography>
                      <Typography variant="body2"><strong>Branch:</strong> {branchInfo.branchName || branchName}</Typography>
                      <Typography variant="body2"><strong>Date:</strong> {new Date().toLocaleString()}</Typography>
                      <Typography variant="body2"><strong>Order ID:</strong> POS{orderId}</Typography>
                      <Typography variant="body2"><strong>Cashier:</strong> {decodedToken.Username || "Cashier"}</Typography>
                      <Typography variant="body2"><strong>Customer:</strong> {selectedCustomer?.customerName || newCustomerName || "Cash Customer"}</Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      {cart.map((item, index) => (
                        <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: "1px solid #eee" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <Box>
                              <Typography variant="body1" fontWeight="bold">{item.name || item.productName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.productCode} | Qty: {item.quantity} {isWarehouse ? "Cartons" : "Pairs"} | Size: {item.size || item.productSize || 0} | Color: {item.colorName || item.productColor || " "}
                              </Typography>
                            </Box>
                            <Typography variant="body1" fontWeight="bold">KSh {((item.averageSellingPrice || item.averageBP || 0) * item.quantity).toFixed(2)}</Typography>
                          </Box>
                          <Typography variant="caption">{item.quantity} × KSh {item.averageSellingPrice || item.averageBP || 0}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Paper sx={{ p: 2, mb: 2, bgcolor: "#E3F2FD", border: `1px solid ${theme.bank}` }}>
                      <Typography variant="subtitle2" fontWeight="bold" color={theme.bank} gutterBottom>Payment Breakdown</Typography>
                      {selectedPaymentMethods.map((method, index) => (
                        <Box key={index} sx={{ display: "flex", justifyContent: "space-between", ml: 2, mb: 1 }}>
                          <Typography variant="body2">{method.charAt(0).toUpperCase() + method.slice(1)}:</Typography>
                          <Typography variant="body2" fontWeight="bold">KSh {(parseFloat(amounts[method]) || 0).toFixed(2)}</Typography>
                        </Box>
                      ))}
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body1" fontWeight="bold">Total Paid:</Typography>
                        <Typography variant="body1" fontWeight="bold" color={theme.success}>KSh {getTotalSplit().toFixed(2)}</Typography>
                      </Box>
                    </Paper>
                    <Box sx={{ borderTop: "2px solid #000", pt: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="h6"><strong>GRAND TOTAL:</strong></Typography>
                        <Typography variant="h5" fontWeight="bold" color={theme.primary}>KSh {totalAmount.toFixed(2)}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: "center", mt: 3, pt: 2, borderTop: "1px dashed #000" }}>
                      <Typography variant="body2" fontWeight="bold">Thank you for your business!</Typography>
                      <Typography variant="caption">Receipt ID: POS{orderId}</Typography>
                    </Box>
                  </Paper>
                </CardContent>
              </Card>
            )}

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
              <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => handlePrintReceipt(activeTab === 0 ? "simple" : "detailed")}>View Full Receipt</Button>
              <Button variant="contained" onClick={handleReceiptDone} sx={{ bgcolor: theme.success }}>Done</Button>
            </Box>
          </DialogContent>
        ) : (
          <>
            <DialogContent sx={{ mt: 2 }}>
              {/* Credit Repayment Banner */}
              {isCredit && (
                <Zoom in={true}>
                  <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: "#FFF8E1", borderRadius: 2, border: `2px solid #FFA726` }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <CreditScoreIcon sx={{ color: "#FFA726", mr: 1, fontSize: 24 }} />
                      <Typography variant="subtitle1" fontWeight="700" sx={{ color: "#E65100" }}>Credit Repayment</Typography>
                      <Chip label="Outstanding Balance" size="small" sx={{ ml: 1, bgcolor: "#FFA726", color: "white", fontWeight: "600" }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      The customer is repaying an outstanding credit balance. Select a payment method and enter the amount.
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="body2" fontWeight="600">Outstanding Amount:</Typography>
                      <Typography variant="h6" fontWeight="700" sx={{ color: "#E65100" }}>KSh {saleOrderPaymentAmount.toFixed(2)}</Typography>
                    </Box>
                    {getTotalSplit() > 0 && getTotalSplit() < totalAmount && (
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2" fontWeight="600" color="text.secondary">Remaining after this payment:</Typography>
                        <Typography variant="body1" fontWeight="700" sx={{ color: "#E65100" }}>KSh {(totalAmount - getTotalSplit()).toFixed(2)}</Typography>
                      </Box>
                    )}
                  </Paper>
                </Zoom>
              )}

              {/* Order Summary */}
              <Paper elevation={0} sx={{ p: 2, bgcolor: theme.lighter, borderRadius: 2, mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {isCredit ? "Credit Repayment Summary" : `Order Summary (Account: POS${orderId})`}
                </Typography>
                {isCredit ? (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2">Order Reference:</Typography>
                      <Typography variant="body2" fontWeight="600">POS{orderId}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="h6" fontWeight="600">Outstanding Amount:</Typography>
                      <Typography variant="h5" fontWeight="700" color="#E65100">KSh {totalAmount.toFixed(2)}</Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2">Total Items:</Typography>
                      <Typography variant="body2" fontWeight="600">
                        {cart.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0)} {isWarehouse ? "Cartons" : "Pairs"}
                      </Typography>
                    </Box>
                    {discountInfo.hasDiscounts && (
                      <>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <Typography variant="body2">Subtotal:</Typography>
                          <Typography variant="body2">KSh {discountInfo.subtotal.toFixed(2)}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, color: theme.discount }}>
                          <Typography variant="body2" fontWeight="600">Discount:</Typography>
                          <Typography variant="body2" fontWeight="600">-KSh {discountInfo.totalDiscounts.toFixed(2)}</Typography>
                        </Box>
                      </>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="h6" fontWeight="600">Total Amount:</Typography>
                      <Typography variant="h5" fontWeight="700" color={theme.dark}>KSh {totalAmount.toFixed(2)}</Typography>
                    </Box>
                  </>
                )}
              </Paper>

              {/* Payment Methods */}
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>Select Payment Methods</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { value: "cash", label: "Cash", icon: <MoneyIcon /> },
                  { value: "mpesa", label: "M-Pesa", icon: <PhoneAndroidIcon /> },
                  { value: "code", label: "M-Pesa Code", icon: <ReceiptIcon /> },
                  { value: "bank", label: "Bank", icon: <AccountBalanceIcon /> },
                  ...(!isCredit ? [{ value: "credit", label: "Credit", icon: <CreditScoreIcon /> }] : []),
                ].map((method) => (
                  <Grid item xs={6} sm={2.4} key={method.value}>
                    <Paper elevation={selectedPaymentMethods.includes(method.value) ? 3 : 0}
                      sx={{
                        p: 2, textAlign: "center", cursor: "pointer", transition: "all 0.2s",
                        bgcolor: selectedPaymentMethods.includes(method.value) ? (method.value === "bank" ? "#E3F2FD" : theme.lighter) : "#F8F9FA",
                        border: selectedPaymentMethods.includes(method.value) ? `2px solid ${method.value === "bank" ? theme.bank : theme.primary}` : "2px solid transparent",
                        "&:hover": { bgcolor: method.value === "bank" ? "#E3F2FD" : theme.lighter, transform: "translateY(-2px)" },
                      }}
                      onClick={() => handlePaymentMethodToggle(method.value)}>
                      <Box sx={{ color: selectedPaymentMethods.includes(method.value) ? (method.value === "bank" ? theme.bank : theme.dark) : "#7F8C8D", mb: 1 }}>
                        {React.cloneElement(method.icon, { sx: { fontSize: 32 } })}
                      </Box>
                      <Typography variant="caption" fontWeight={selectedPaymentMethods.includes(method.value) ? "600" : "400"}
                        sx={{ color: selectedPaymentMethods.includes(method.value) ? (method.value === "bank" ? theme.bank : theme.dark) : "text.secondary" }}>
                        {method.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Customer Selection (new credit sales only) */}
              {needsCustomer && !isCredit && (
                <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: "#F8F9FA", borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    Customer Information{" "}
                    {selectedPaymentMethods.includes("credit") && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
                  </Typography>
                  <Autocomplete
                    options={selectedPaymentMethods.includes("credit") ? filteredCustomers : customerOptions}
                    getOptionLabel={(option) => option.customerName || ""}
                    value={selectedCustomer}
                    onChange={(e, value) => { setSelectedCustomer(value); setPhoneNumber(value?.phoneNumber || ""); }}
                    inputValue={searchInput}
                    onInputChange={(e, value) => setSearchInput(value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Search Existing Customer" size="small"
                        error={selectedPaymentMethods.includes("credit") && !selectedCustomer && !newCustomerName}
                        helperText={selectedPaymentMethods.includes("credit") && !selectedCustomer && !newCustomerName ? "Customer is required for credit payment" : ""} />
                    )}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Divider sx={{ flex: 1 }} />
                    <IconButton size="small" onClick={() => setShowNewCustomer(!showNewCustomer)}>
                      {showNewCustomer ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <Typography variant="caption" sx={{ mx: 1 }}>Create New Customer</Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>
                  <Collapse in={showNewCustomer}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField label="Customer Name" size="small" fullWidth value={newCustomerName} onChange={(e) => setNewCustomerName(e.target.value)} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField label="Phone Number" size="small" fullWidth value={newPhoneNumber} onChange={(e) => setNewPhoneNumber(e.target.value)} />
                      </Grid>
                    </Grid>
                  </Collapse>
                </Paper>
              )}

              {/* Payment Method Details */}
              {selectedPaymentMethods.length > 0 && (
                <Stack spacing={2}>
                  {selectedPaymentMethods.map((method) => (
                    <Paper key={method} elevation={1} sx={{ p: 2, borderLeft: `4px solid ${method === "bank" ? theme.bank : theme.primary}` }}>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>{method.toUpperCase()} Payment</Typography>

                      {method === "cash" && (
                        <>
                          <TextField label={isCredit ? "Amount" : "Required Cash Amount"} type="number" size="small" fullWidth
                            value={amounts.cash} onChange={(e) => handleAmountChange("cash", e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }} sx={{ mb: 2 }}
                            InputProps={{ startAdornment: <InputAdornment position="start">KSh</InputAdornment> }}
                            helperText={isCredit ? "Enter amount to pay via cash" : "Amount to be paid via cash"} />
                          {requireMpesaCodeForCash && (
                            <TextField
                              label="M-Pesa Code (Cash Reconciliation)"
                              size="small"
                              fullWidth
                              value={cashMpesaCode}
                              onChange={(e) => setCashMpesaCode(e.target.value.toUpperCase())}
                              placeholder="e.g., QA12BC3DEF"
                              sx={{ mb: 2 }}
                              helperText="Enter the M-Pesa transaction code for reconciliation"
                            />
                          )}
                          {!isCredit && (
                            <Paper elevation={0} sx={{ p: 2, bgcolor: "#E3F2FD", borderRadius: 2, border: "2px solid #2196F3" }}>
                              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                <AccountBalanceWalletIcon sx={{ color: "#2196F3", mr: 1 }} />
                                <Typography variant="subtitle2" fontWeight="600">Cash Received from Customer</Typography>
                              </Box>
                              <TextField label="Amount Customer Gave" type="number" size="small" fullWidth value={cashTendered}
                                onChange={(e) => setCashTendered(e.target.value)} inputProps={{ min: 0, step: 0.01 }}
                                InputProps={{ startAdornment: <InputAdornment position="start">KSh</InputAdornment> }}
                                sx={{ mb: 2, bgcolor: "white" }}
                                placeholder={`Enter amount (Required: ${(parseFloat(amounts.cash) || totalAmount).toFixed(2)})`}
                                helperText="Enter the exact amount the customer handed over" />
                              {showChangeAlert && (
                                <Fade in={true}>
                                  <Paper elevation={0} sx={{ p: 2, bgcolor: change >= 0 ? "#E8F5E9" : "#FFEBEE", border: `3px solid ${change >= 0 ? theme.success : theme.error}`, borderRadius: 2 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                      <Typography variant="body2" color="text.secondary">Required Amount:</Typography>
                                      <Typography variant="body1" fontWeight="700">KSh {(parseFloat(amounts.cash) || totalAmount).toFixed(2)}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                      <Typography variant="body2" color="text.secondary">Customer Gave:</Typography>
                                      <Typography variant="body1" fontWeight="700">KSh {parseFloat(cashTendered).toFixed(2)}</Typography>
                                    </Box>
                                    <Divider sx={{ my: 1.5, borderWidth: 1 }} />
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.5, bgcolor: change >= 0 ? "#C8E6C9" : "#FFCDD2", borderRadius: 1 }}>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {change >= 0 ? <CheckCircleIcon sx={{ color: theme.success, fontSize: 28 }} /> : <WarningIcon sx={{ color: theme.error, fontSize: 28 }} />}
                                        <Typography variant="h6" fontWeight="700" sx={{ color: change >= 0 ? theme.success : theme.error }}>
                                          {change >= 0 ? "Change to Return:" : "Still Needed:"}
                                        </Typography>
                                      </Box>
                                      <Typography variant="h4" fontWeight="700" sx={{ color: change >= 0 ? theme.success : theme.error }}>
                                        KSh {Math.abs(change).toFixed(2)}
                                      </Typography>
                                    </Box>
                                    {change < 0 && <Alert severity="error" sx={{ mt: 2 }} icon={<WarningIcon />}><Typography variant="body2" fontWeight="600">Customer needs to provide KSh {Math.abs(change).toFixed(2)} more</Typography></Alert>}
                                    {change > 0 && <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}><Typography variant="body2" fontWeight="600">Return KSh {change.toFixed(2)} to the customer</Typography></Alert>}
                                  </Paper>
                                </Fade>
                              )}
                            </Paper>
                          )}
                        </>
                      )}

                      {method === "bank" && (
                        <Paper elevation={0} sx={{ p: 2, bgcolor: "#E3F2FD", borderRadius: 2, border: `2px solid ${theme.bank}` }}>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <AccountBalanceIcon sx={{ color: theme.bank, mr: 1, fontSize: 24 }} />
                            <Typography variant="subtitle2" fontWeight="600" sx={{ color: theme.bank }}>Bank Transfer Details</Typography>
                          </Box>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <TextField label="Bank Amount" type="number" size="small" fullWidth value={amounts.bank}
                                onChange={(e) => handleAmountChange("bank", e.target.value)}
                                inputProps={{ min: 0, step: 0.01 }}
                                InputProps={{ startAdornment: <InputAdornment position="start">KSh</InputAdornment> }}
                                sx={{ bgcolor: "white" }} helperText="Amount paid via bank transfer" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField label="Bank Reference Number (Optional)" size="small" fullWidth value={bankReference}
                                onChange={(e) => setBankReference(e.target.value.toUpperCase())}
                                placeholder="e.g., TXN1234567890" sx={{ bgcolor: "white" }}
                                helperText="Transaction/reference number from bank"
                                InputProps={{ startAdornment: <InputAdornment position="start"><AccountBalanceIcon sx={{ fontSize: 18, color: theme.bank }} /></InputAdornment> }} />
                            </Grid>
                          </Grid>
                          <Alert severity="info" icon={<AccountBalanceIcon />} sx={{ mt: 2, bgcolor: "white" }}>
                            <Typography variant="caption">Please verify the bank transfer has been received. Reference number is optional but recommended.</Typography>
                          </Alert>
                        </Paper>
                      )}

                      {/* {method === "mpesa" && (
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField label="Amount" type="number" size="small" fullWidth value={amounts.mpesa} onChange={(e) => handleAmountChange("mpesa", e.target.value)} />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField label="Phone Number" size="small" fullWidth value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="0712345678" />
                          </Grid>
                        </Grid>
                      )} */}
                      {method === "mpesa" && (
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField label="Amount" type="number" size="small" fullWidth value={amounts.mpesa} onChange={(e) => handleAmountChange("mpesa", e.target.value)} />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label="Phone Number"
                              size="small"
                              fullWidth
                              value={phoneNumber}
                              onChange={(e) => handlePhoneChange(e.target.value)}
                              placeholder="0712345678"
                              error={Boolean(phoneError)}
                              helperText={phoneError || " "}
                            />
                          </Grid>
                        </Grid>
                      )}

                      {method === "credit" && (
                        <TextField label="Credit Amount" type="number" size="small" fullWidth value={amounts.credit}
                          onChange={(e) => handleAmountChange("credit", e.target.value)} placeholder="Amount to pay later" />
                      )}

                      {method === "code" && (
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField label="M-Pesa Code" size="small" fullWidth value={referenceCode}
                              onChange={(e) => setReferenceCode(e.target.value.toUpperCase())} placeholder="QA12BC3DEF" />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField label="Amount" type="number" size="small" fullWidth value={amounts.code} onChange={(e) => handleAmountChange("code", e.target.value)} />
                          </Grid>
                        </Grid>
                      )}
                    </Paper>
                  ))}

                  {/* Split Summary */}
                  <Paper elevation={0} sx={{
                    p: 2,
                    bgcolor: getTotalSplit() > totalAmount ? "#FFE5E5" : getTotalSplit() > 0 ? "#D5F4E6" : theme.lighter,
                    border: `2px solid ${getTotalSplit() > totalAmount ? "#FF5252" : getTotalSplit() > 0 ? theme.success : theme.primary}`,
                  }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" fontWeight="600">{isCredit ? "Paying Now:" : "Total Split:"}</Typography>
                      <Typography variant="body2" fontWeight="600">KSh {getTotalSplit().toFixed(2)} / {totalAmount.toFixed(2)}</Typography>
                    </Box>
                    {getTotalSplit() > totalAmount && (
                      <Typography variant="caption" color="#FF5252">Payment exceeds outstanding balance by KSh {(getTotalSplit() - totalAmount).toFixed(2)}</Typography>
                    )}
                    {getTotalSplit() > 0 && getTotalSplit() <= totalAmount && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CheckCircleIcon sx={{ color: theme.success, fontSize: 20 }} />
                        <Typography variant="caption" color={theme.success}>
                          {isCredit
                            ? getTotalSplit() < totalAmount
                              ? `Partial payment — KSh ${(totalAmount - getTotalSplit()).toFixed(2)} will remain outstanding`
                              : "Full balance cleared"
                            : "Payment amount matches total"}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Stack>
              )}

              {/* Cart Items (new sales only) */}
              {!isCredit && cart.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ mt: 3, mb: 2 }}>Items ({cart.length})</Typography>
                  <Paper elevation={0} sx={{ maxHeight: 200, overflowY: "auto", border: "1px solid #E0E0E0", borderRadius: 1 }}>
                    <Stack divider={<Divider />}>
                      {cart.map((item, index) => (
                        <Box key={index} sx={{ p: 1.5 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Typography variant="body2" fontWeight="600">{item.name || item.productName} ({item.productCode})</Typography>
                                {item.isDiscount && (
                                  <Chip label={`-KSh ${item.discountValue.toFixed(2)}`} size="small"
                                    sx={{ bgcolor: theme.discount, color: "white", height: 20, fontSize: 10 }}
                                    icon={<DiscountIcon sx={{ color: "white !important", fontSize: 12 }} />} />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                Qty: {item.quantity} {isWarehouse ? "Cartons" : "Pairs"} × KSh {isWarehouse ? item.averageBP : item.averageSellingPrice}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: "right" }}>
                              {item.isDiscount ? (
                                <>
                                  <Typography variant="caption" sx={{ textDecoration: "line-through", color: "text.secondary", display: "block" }}>KSh {(item.originalAmount || item.totalPrice).toFixed(2)}</Typography>
                                  <Typography variant="body2" fontWeight="600" sx={{ color: theme.discount }}>KSh {item.netAmount.toFixed(2)}</Typography>
                                </>
                              ) : (
                                <Typography variant="body2" fontWeight="600">KSh {(item.totalPrice || 0).toFixed(2)}</Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 3, bgcolor: "#F8F9FA" }}>
              <Button onClick={handleClose} variant="outlined" size="large" disabled={loading || isConfirming}>Cancel</Button>
              <Button onClick={handleConfirmPayment} variant="contained" size="large" disabled={isPaymentDisabled}
                sx={{ bgcolor: theme.primary, "&:hover": { bgcolor: theme.dark }, px: 4 }}>
                {loading || isConfirming ? (
                  <><CircularProgress size={20} sx={{ mr: 1, color: "white" }} />{isConfirming ? "Confirming M-Pesa..." : "Processing..."}</>
                ) : (
                  selectedPaymentMethods.includes("credit") && !isCredit ? "Create Credit Invoice" : "Confirm Payment"
                )}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
        <Dialog open={showMpesaVerifyDialog} onClose={handleCloseVerifyDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: theme.primary, color: "white", display: "flex", alignItems: "center", gap: 1 }}>
            <PhoneAndroidIcon />
            <Typography variant="h6" fontWeight="600">Verify M-Pesa Payment</Typography>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Looking for a matching incoming M-Pesa transaction of <strong>KSh {pendingMpesaAmount.toFixed(2)}</strong>.
              Select it below to confirm immediately, or wait — payment will also confirm automatically.
            </Typography>

            {suspendedC2BLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!suspendedC2BLoading && suspendedC2BResults.length === 0 && (
              <Alert severity="info">No matching transaction found yet. Try refreshing in a moment.</Alert>
            )}

            {!suspendedC2BLoading && suspendedC2BResults.length > 0 && (
              <Stack spacing={1.5}>
                {suspendedC2BResults.map((txn) => (
                  <Paper
                    key={txn.id || txn.transID}
                    elevation={0}
                    sx={{ p: 2, border: "1px solid #E0E0E0", borderRadius: 2, cursor: "pointer", "&:hover": { bgcolor: theme.lighter } }}
                    onClick={() => handleSelectSuspendedTransaction(txn)}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="body2" fontWeight="700">{txn.firstName} {txn.lastName}</Typography>
                      <Typography variant="body2" fontWeight="700" color={theme.success}>KSh {txn.transAmount}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">Name: {txn.firstName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">Code: {txn.transID}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Time: {formatTransactionTime(txn.transTime)}
                    </Typography>                    
                  </Paper>
                ))}
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCancelMpesaVerification} color="error">Cancel</Button>
            <Button onClick={() => fetchSuspendedC2BTransactions(pendingMpesaAmount)} disabled={suspendedC2BLoading}>
              Refresh
            </Button>
            {/* <Button onClick={handleCloseVerifyDialog} variant="outlined">Keep Waiting</Button> */}
          </DialogActions>
        </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={20000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Payment;